/**
 * ENVIRONMENTAL CALCULATIONS UTILITY
 * Implements all formulas from ENVIRONMENTAL_INDICATORS_KNOWLEDGE_BASE.md
 * Based on peer-reviewed scientific research
 */

import { Tree } from '../types/tree';
import SPECIES_DATABASE, { PUNE_CONSTANTS, SpeciesData } from '../data/speciesFormulas';

// ============================================
// 1. OXYGENATION METRICS
// ============================================

export interface OxygenMetrics {
  totalOxygenKg: number;
  totalOxygenTonnes: number;
  totalOxygenLiters: number;
  peopleSupported: number;
  cityContributionPct: number;
  medicalCylinders: number;
  healthValueMin: number;
  healthValueMax: number;
  healthValueAvg: number;
  treesPerPerson: number;
}

/**
 * Calculate oxygen production for all trees
 * Source: Nowak et al. (2007), USDA Forest Service
 * Default: 100 kg O₂/tree/year for mature trees (GBH > 30 cm)
 */
export function calculateOxygenMetrics(
  trees: Tree[],
  cityPopulation: number = 4570000, // Pune 2025
  oxygenRatePerTree: number = 100 // kg/year (moderate estimate)
): OxygenMetrics {
  // Filter mature trees (GBH > 30 cm)
  const matureTrees = trees.filter(tree => tree.GBH && Number(tree.GBH) > 30);
  const matureCount = matureTrees.length;

  // Calculate species-specific oxygen where possible
  let totalOxygenKg = 0;
  matureTrees.forEach(tree => {
    // For now, use default rate for all trees
    // Species-specific rates can be added when SPECIES_DATABASE structure is mapped
    totalOxygenKg += oxygenRatePerTree;
  });

  // Conversions
  const totalOxygenTonnes = totalOxygenKg / 1000;
  const totalOxygenLiters = totalOxygenKg * 700; // 1 kg O₂ = 700 liters at STP

  // Human oxygen requirement: 287 kg/year per person
  const peopleSupported = totalOxygenKg / 287;
  const cityContributionPct = (peopleSupported / cityPopulation) * 100;

  // Medical cylinder equivalent: 680 liters per cylinder
  const medicalCylinders = totalOxygenLiters / 680;

  // Health economic valuation (₹500-1000 per cylinder)
  const healthValueMin = medicalCylinders * 500;
  const healthValueMax = medicalCylinders * 1000;
  const healthValueAvg = medicalCylinders * 750;

  // Trees required per person
  const treesPerPerson = 287 / oxygenRatePerTree;

  return {
    totalOxygenKg,
    totalOxygenTonnes,
    totalOxygenLiters,
    peopleSupported: Math.round(peopleSupported),
    cityContributionPct,
    medicalCylinders: Math.round(medicalCylinders),
    healthValueMin,
    healthValueMax,
    healthValueAvg,
    treesPerPerson,
  };
}

/**
 * Get oxygen production scenarios (conservative, moderate, optimistic)
 */
export function getOxygenScenarios(trees: Tree[], cityPopulation: number = 4570000) {
  return {
    conservative: calculateOxygenMetrics(trees, cityPopulation, 80),
    moderate: calculateOxygenMetrics(trees, cityPopulation, 100),
    optimistic: calculateOxygenMetrics(trees, cityPopulation, 120),
  };
}

// ============================================
// 2. CARBON SEQUESTRATION & CLIMATE IMPACT
// ============================================

export interface CarbonMetrics {
  totalCarbonKg: number;
  totalCarbonTonnes: number;
  totalCO2Tonnes: number;
  totalBiomassKg: number;
  totalBiomassTonnes: number;
  totalAGBKg: number;
  totalAGBTonnes: number;
  totalBGBKg: number;
  totalBGBTonnes: number;
  agbProportion: number;
  bgbProportion: number;
  vehiclesOffset: number;
  householdsOffset: number;
  flightsOffset: number;
  carbonValueINR: number;
  carbonValueEUR: number;
  annualSequestrationKg: number;
  annualSequestrationTonnes: number;
}

/**
 * Calculate carbon sequestration metrics
 * Source: EPA, EU ETS, ISEC, Curb6
 */
export function calculateCarbonMetrics(
  trees: Tree[],
  euEtsPrice: number = 76.67, // EUR per tonne CO₂e (Sept 2025)
  eurToInr: number = 103 // Exchange rate
): CarbonMetrics {
  // CSV values are already in tonnes, not kg - sum them directly
  let totalCarbonTonnes = 0;
  let totalCO2Tonnes = 0;
  let totalBiomassTonnes = 0;
  let totalAGBTonnes = 0;
  let totalBGBTonnes = 0;

  trees.forEach(tree => {
    if (tree.Carbon) {
      totalCarbonTonnes += Number(tree.Carbon);
    }
    if (tree.co2) {
      totalCO2Tonnes += Number(tree.co2);
    }
    if (tree.Biomass) {
      totalBiomassTonnes += Number(tree.Biomass);
    }
    if (tree.AGB) {
      totalAGBTonnes += Number(tree.AGB);
    }
    if (tree.BGB) {
      totalBGBTonnes += Number(tree.BGB);
    }
  });

  // Convert tonnes to kg for backward compatibility
  const totalCarbonKg = totalCarbonTonnes * 1000;
  const totalCO2Kg = totalCO2Tonnes * 1000;
  const totalBiomassKg = totalBiomassTonnes * 1000;
  const totalAGBKg = totalAGBTonnes * 1000;
  const totalBGBKg = totalBGBTonnes * 1000;

  // Calculate AGB and BGB proportions
  const agbProportion = totalBiomassKg > 0 ? (totalAGBKg / totalBiomassKg) : 0;
  const bgbProportion = totalBiomassKg > 0 ? (totalBGBKg / totalBiomassKg) : 0;

  // Vehicle offset: 4.6 tonnes CO₂ per passenger car per year
  const vehiclesOffset = totalCO2Tonnes / 4.6;

  // Household offset: Using average Indian household (6.5 tonnes CO₂/year)
  const householdsOffset = totalCO2Tonnes / 6.5;

  // Flight offset: Delhi-Mumbai round trip = 0.463 tonnes CO₂ per passenger
  const flightsOffset = totalCO2Tonnes / 0.463;

  // Carbon market valuation
  const carbonValueEUR = totalCO2Tonnes * euEtsPrice;
  const carbonValueINR = carbonValueEUR * eurToInr;

  // Annual sequestration (using average tree sequestration rate)
  // Mature tree sequesters ~21 kg CO₂/year on average
  const matureTrees = trees.filter(tree => tree.GBH && Number(tree.GBH) > 30);
  const annualSequestrationKg = matureTrees.length * 21;
  const annualSequestrationTonnes = annualSequestrationKg / 1000;

  return {
    totalCarbonKg,
    totalCarbonTonnes,
    totalCO2Tonnes,
    totalBiomassKg,
    totalBiomassTonnes,
    totalAGBKg,
    totalAGBTonnes,
    totalBGBKg,
    totalBGBTonnes,
    agbProportion,
    bgbProportion,
    vehiclesOffset: Math.round(vehiclesOffset),
    householdsOffset: Math.round(householdsOffset),
    flightsOffset: Math.round(flightsOffset),
    carbonValueINR,
    carbonValueEUR,
    annualSequestrationKg,
    annualSequestrationTonnes,
  };
}

// ============================================
// 3. AIR QUALITY & PUBLIC HEALTH
// ============================================

export interface AirQualityMetrics {
  totalPollutantsKg: number;
  pmRemovalKg: number;
  no2RemovalKg: number;
  o3RemovalKg: number;
  so2RemovalKg: number;
  coRemovalKg: number;
  equivalentAirPurifiers: number;
  healthBenefitValue: number;
}

/**
 * Calculate air pollutant removal capacity
 * Source: Nowak & Heisler, NRPA
 * Average tree removes ~1.46 kg pollutants/year
 */
export function calculateAirQualityMetrics(trees: Tree[]): AirQualityMetrics {
  const treeCount = trees.length;
  const totalPollutantsKg = treeCount * 1.46;

  // Breakdown by pollutant type (approximate percentages)
  const pmRemovalKg = totalPollutantsKg * 0.45; // 45% PM
  const no2RemovalKg = totalPollutantsKg * 0.25; // 25% NO₂
  const o3RemovalKg = totalPollutantsKg * 0.15; // 15% O₃
  const so2RemovalKg = totalPollutantsKg * 0.10; // 10% SO₂
  const coRemovalKg = totalPollutantsKg * 0.05; // 5% CO

  // Equivalent air purifiers (assuming 1 HEPA purifier removes 5kg PM/year)
  const equivalentAirPurifiers = pmRemovalKg / 5;

  // Health benefit valuation (conservative: ₹500/kg pollutant removed)
  const healthBenefitValue = totalPollutantsKg * 500;

  return {
    totalPollutantsKg,
    pmRemovalKg,
    no2RemovalKg,
    o3RemovalKg,
    so2RemovalKg,
    coRemovalKg,
    equivalentAirPurifiers: Math.round(equivalentAirPurifiers),
    healthBenefitValue,
  };
}

// ============================================
// 4. ENERGY & COOLING SERVICES
// ============================================

export interface CoolingMetrics {
  totalCoolingRooms: number;
  energySavingsKWh: number;
  energySavingsINR: number;
  peakLoadReductionKW: number;
  gridReliefValue: number;
}

/**
 * Calculate cooling and energy services
 * Source: Ecobal EU
 * One mature tree = 10 room air conditioners
 */
export function calculateCoolingMetrics(
  trees: Tree[],
  electricityRate: number = 8 // ₹/kWh (average Pune residential rate)
): CoolingMetrics {
  const treeCount = trees.length;
  const totalCoolingRooms = treeCount * 10;

  // Energy savings (each tree saves ~100 kWh per year)
  const energySavingsKWh = treeCount * 100;
  const energySavingsINR = energySavingsKWh * electricityRate;

  // Peak load reduction (0.5 kW per tree during peak hours)
  const peakLoadReductionKW = treeCount * 0.5;

  // Grid relief value (peak electricity cost ₹12/kWh, 8 hours/day, 120 days/year)
  const gridReliefValue = peakLoadReductionKW * 12 * 8 * 120;

  return {
    totalCoolingRooms,
    energySavingsKWh,
    energySavingsINR,
    peakLoadReductionKW,
    gridReliefValue,
  };
}

// ============================================
// 5. STORMWATER MANAGEMENT
// ============================================

export interface StormwaterMetrics {
  annualInterceptionLiters: number;
  annualInterceptionCubicMeters: number;
  floodReductionValue: number;
  waterQualityValue: number;
  infrastructureSavings: number;
  totalEconomicValue: number;
}

/**
 * Calculate stormwater interception benefits
 * Source: McPherson & Xiao (2004), USDA Forest Service
 * Average mature tree intercepts 6,000 liters/year
 */
export function calculateStormwaterMetrics(
  trees: Tree[],
  treatmentCostPerLiter: number = 0.03 // ₹/liter
): StormwaterMetrics {
  const treeCount = trees.length;
  const annualInterceptionLiters = treeCount * 6000;
  const annualInterceptionCubicMeters = annualInterceptionLiters / 1000;

  // Treatment cost savings
  const waterQualityValue = annualInterceptionLiters * treatmentCostPerLiter;

  // Flood reduction value (₹50/cubic meter retained)
  const floodReductionValue = annualInterceptionCubicMeters * 50;

  // Infrastructure savings (avoided gray infrastructure: ₹100/cubic meter)
  const infrastructureSavings = annualInterceptionCubicMeters * 100;

  const totalEconomicValue = waterQualityValue + floodReductionValue + infrastructureSavings;

  return {
    annualInterceptionLiters,
    annualInterceptionCubicMeters,
    floodReductionValue,
    waterQualityValue,
    infrastructureSavings,
    totalEconomicValue,
  };
}

// ============================================
// 6. COMPREHENSIVE ENVIRONMENTAL IMPACT
// ============================================

export interface ComprehensiveImpact {
  oxygen: OxygenMetrics;
  carbon: CarbonMetrics;
  airQuality: AirQualityMetrics;
  cooling: CoolingMetrics;
  stormwater: StormwaterMetrics;
  totalTrees: number;
  matureTrees: number;
  totalEconomicValue: number;
  summary: {
    primaryBenefit: string;
    secondaryBenefit: string;
    totalAnnualValue: number;
  };
}

/**
 * Calculate comprehensive environmental impact
 * Combines all metrics into one assessment
 */
export function calculateComprehensiveImpact(
  trees: Tree[],
  cityPopulation: number = 4570000,
  options: {
    oxygenRate?: number;
    euEtsPrice?: number;
    eurToInr?: number;
    electricityRate?: number;
    treatmentCost?: number;
  } = {}
): ComprehensiveImpact {
  const {
    oxygenRate = 100,
    euEtsPrice = 76.67,
    eurToInr = 103,
    electricityRate = 8,
    treatmentCost = 0.03,
  } = options;

  const oxygen = calculateOxygenMetrics(trees, cityPopulation, oxygenRate);
  const carbon = calculateCarbonMetrics(trees, euEtsPrice, eurToInr);
  const airQuality = calculateAirQualityMetrics(trees);
  const cooling = calculateCoolingMetrics(trees, electricityRate);
  const stormwater = calculateStormwaterMetrics(trees, treatmentCost);

  const matureTrees = trees.filter(tree => tree.GBH && Number(tree.GBH) > 30).length;

  // Total economic value (annual benefits)
  const totalEconomicValue =
    oxygen.healthValueAvg +
    carbon.carbonValueINR +
    airQuality.healthBenefitValue +
    cooling.energySavingsINR +
    cooling.gridReliefValue +
    stormwater.totalEconomicValue;

  // Determine primary and secondary benefits
  const benefits = [
    { name: 'Carbon Sequestration', value: carbon.carbonValueINR },
    { name: 'Oxygen Production', value: oxygen.healthValueAvg },
    { name: 'Air Quality', value: airQuality.healthBenefitValue },
    { name: 'Cooling Services', value: cooling.energySavingsINR + cooling.gridReliefValue },
    { name: 'Stormwater Management', value: stormwater.totalEconomicValue },
  ];

  benefits.sort((a, b) => b.value - a.value);

  return {
    oxygen,
    carbon,
    airQuality,
    cooling,
    stormwater,
    totalTrees: trees.length,
    matureTrees,
    totalEconomicValue,
    summary: {
      primaryBenefit: benefits[0].name,
      secondaryBenefit: benefits[1].name,
      totalAnnualValue: totalEconomicValue,
    },
  };
}

// ============================================
// 7. UTILITY FUNCTIONS
// ============================================

/**
 * Format large numbers to Indian number system
 */
export function formatIndianNumber(num: number): string {
  const isNegative = num < 0;
  num = Math.abs(num);

  if (num >= 10000000) {
    return `${isNegative ? '-' : ''}${(num / 10000000).toFixed(2)} Crore`;
  } else if (num >= 100000) {
    return `${isNegative ? '-' : ''}${(num / 100000).toFixed(2)} Lakh`;
  } else if (num >= 1000) {
    return `${isNegative ? '-' : ''}${(num / 1000).toFixed(2)} Thousand`;
  } else {
    return `${isNegative ? '-' : ''}${num.toFixed(2)}`;
  }
}

/**
 * Format currency in Indian Rupees
 */
export function formatCurrency(amount: number): string {
  return `₹${formatIndianNumber(amount)}`;
}

/**
 * Format percentage with precision
 */
export function formatPercentage(value: number, decimals: number = 4): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Get size category from GBH (Girth at Breast Height)
 */
export function getTreeSizeCategory(gbh: number): string {
  if (gbh < 30) return 'Small';
  if (gbh < 94) return 'Medium';
  if (gbh < 239) return 'Large';
  return 'Extra Large';
}

/**
 * Get oxygen production rate by tree size
 * Source: Nowak et al. (2007)
 */
export function getOxygenRateBySize(gbh: number): number {
  if (gbh < 30) return 15; // Small: 5-25 kg (average 15)
  if (gbh < 94) return 37.5; // Medium: 25-50 kg (average 37.5)
  if (gbh < 239) return 75; // Large: 50-100 kg (average 75)
  return 110; // Extra-large: 100-120 kg (average 110)
}

/**
 * Calculate age-adjusted metrics
 * Younger trees have lower productivity
 */
export function getAgeAdjustmentFactor(gbh: number): number {
  if (gbh < 30) return 0.3; // 30% productivity
  if (gbh < 60) return 0.6; // 60% productivity
  if (gbh < 94) return 0.85; // 85% productivity
  return 1.0; // 100% productivity
}

/**
 * Calculate ecological value score (0-100)
 */
export function calculateEcologicalValue(tree: Tree): number {
  let score = 0;

  // Size contribution (max 30 points)
  if (tree.GBH) {
    if (Number(tree.GBH) > 239) score += 30;
    else if (Number(tree.GBH) > 94) score += 25;
    else if (Number(tree.GBH) > 30) score += 15;
    else score += 5;
  }

  // Carbon sequestration (max 25 points)
  if (tree.co2) {
    const co2Value = Number(tree.co2);
    if (co2Value > 100) score += 25;
    else if (co2Value > 50) score += 20;
    else if (co2Value > 20) score += 15;
    else score += 10;
  }

  // Conservation status (max 25 points)
  if (Number(tree.Native) === 1) score += 10;
  if (Number(tree.Endemism) === 1) score += 10;
  if (tree.Threat_status) score += 5;

  // Ecological benefits (max 10 points)
  if (tree.Uses) score += 5; // Human utility
  if (tree.Canopy) score += 5; // Shade provision

  return Math.min(score, 100);
}

/**
 * Get seasonal activity (flowering/fruiting) for current month
 * Note: Flowering/Fruiting data not available in current dataset
 */
export function getCurrentSeasonalActivity(_tree: Tree): {
  isFlowering: boolean;
  isFruiting: boolean;
} {
  // Seasonal data not available
  return { isFlowering: false, isFruiting: false };
}

/**
 * Get all months when tree is flowering/fruiting
 */
export function getSeasonalMonths(timeString: string): number[] {
  if (!timeString) return [];
  return timeString.split(',').map(m => parseInt(m.trim())).filter(m => !isNaN(m));
}

/**
 * Month names for display
 */
export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export const MONTH_NAMES_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];
